import { faBan, faCircleExclamation, faPen, faUserCheck } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import React, { useContext, useState } from "react";
import { Card, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { AppRulesContext } from "../../App";
import UsersApi from "../../api/UsersApi";
import { EActionItem, EToastvariant, IUserResponse } from "../../config/interfaces";
import { useAppDispatch } from "../../redux/Store";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";
import { UserTableHeadings } from "../TableHeaders";
import { ConfirmationModal } from "../modals";
import RowActionOverlay from "../overlay/RowActionOverlay";
import TablePagination from "../pagination/TablePagination";
import NoRecordFound from "../placeholders/NoRecordFound";

type Props = {
  usersData: IUserResponse[];
  pageCount: number;
  forcePage: number;
  handleOnPageChange: (event: any) => void;
  handleRefresh: () => void;
  noRecords: boolean;
  hideColumns?: string[];
  pageName?: string;
};

const UsersTable: React.FC<Props> = (props: Props) => {
  const {
    usersData,
    pageCount,
    forcePage,
    handleOnPageChange,
    handleRefresh,
    noRecords = false,
    hideColumns,
    pageName
  } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { rulesSvc } = useContext(AppRulesContext);

  const [showStatusModal, setShowStatusModal] = useState<IUserResponse | null>(null);
  const [isPerformingAction, setIsPerformingAction] = useState<EActionItem>(EActionItem.NULL);

  const handleChangeUserStatus = async () => {
    try {
      if (!showStatusModal) {
        return;
      }

      setIsPerformingAction(EActionItem.TOGGLE_STATUS);
      let _msg = `${showStatusModal?.fullName} inactivated successfully`;
      const adminUsersApi = UsersApi.getInstance();

      if (showStatusModal.inactive) {
        await adminUsersApi.updateUserStatusActive(`${showStatusModal.uuid}`);
        _msg = `${showStatusModal?.fullName} activated successfully`;
      } else {
        await adminUsersApi.updateUserStatusInactive(`${showStatusModal.uuid}`);
      }

      handleRefresh();

      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Success",
          message: _msg,
          variant: EToastvariant.Success
        })
      );
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsPerformingAction(EActionItem.NULL);
      setShowStatusModal(null);
    }
  };

  return (
    <>
      <Card className="table-card">
        <Card.Body>
          <Table
            hover={noRecords ? false : rulesSvc.canAccess(rulesSvc.usersList) ? true : true}
            responsive
          >
            <thead>
              <tr>
                {UserTableHeadings.map(
                  (_heading, index) =>
                    !hideColumns?.includes(UserTableHeadings[index].title) && (
                      <th
                        className={`${_heading.classPrefix}${_heading.size}`}
                        key={`user-th-${index}`}
                      >
                        {_heading.title}
                      </th>
                    )
                )}
              </tr>
            </thead>
            <tbody>
              {!noRecords ? (
                <>
                  {usersData.map((user, index) => (
                    <tr
                      key={`user-${index}`}
                      onClick={() =>
                        rulesSvc.canAccess(rulesSvc.usersList)
                          ? navigate(`/${pageName}/${user.uuid}`)
                          : null
                      }
                      className={classNames(user?.inactive && "bg-dangerLight")}
                    >
                      {!hideColumns?.includes(UserTableHeadings[0].title) && (
                        <td className="text-capitalize">{user?.fullName}</td>
                      )}
                      {!hideColumns?.includes(UserTableHeadings[1].title) && (
                        <td>{user?.email || "-"}</td>
                      )}
                      {!hideColumns?.includes(UserTableHeadings[2].title) && (
                        <td>{user?.totalFilings || "-"}</td>
                      )}
                      {!hideColumns?.includes(UserTableHeadings[3].title) && (
                        <td
                          className={classNames(
                            "fw-bold",
                            user?.inactive ? "text-danger" : "text-success"
                          )}
                        >
                          {user?.inactive ? "Inactive" : "Active"}
                        </td>
                      )}
                      {!hideColumns?.includes(UserTableHeadings[4].title) && (
                        <td>{UtilService.getDateTimeDisplay(user?.createdAt) || "-"}</td>
                      )}
                      {/* {!hideColumns?.includes(UserTableHeadings[4].title) && (
                        <td>{user?.role ? user.role : "-"}</td>
                      )} */}
                      {!hideColumns?.includes(UserTableHeadings[5].title) && (
                        <td
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <RowActionOverlay
                            popoverEvents={[
                              {
                                icon: faPen,
                                title: "Edit",
                                action: () => navigate(`/${pageName}/${user.uuid}`),
                                actionRules: rulesSvc.updateUser
                              },
                              {
                                icon: user?.inactive ? faUserCheck : faBan,
                                title: user?.inactive ? "Activate" : "Inactivate",
                                btnClass: user?.inactive ? "text-success" : "text-danger",
                                action: () => setShowStatusModal(() => user),
                                actionRules: rulesSvc.userStatusUpdate
                              }
                            ]}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </>
              ) : (
                <NoRecordFound colspan={UserTableHeadings.length} />
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {!noRecords && (
        <TablePagination
          pageCount={pageCount}
          pageOffset={forcePage}
          handleOnPageChange={handleOnPageChange}
        />
      )}

      {showStatusModal && (
        <ConfirmationModal
          showModal={!!showStatusModal}
          icon={faCircleExclamation}
          iconColorClass="text-warning"
          onYesBtnClick={() => handleChangeUserStatus()}
          onNoBtnClick={() => {
            setShowStatusModal(null);
          }}
          title="Please confirm"
          message={
            <p>
              Are you sure you want to {showStatusModal?.inactive ? "Activate" : "Inactivate"} "
              {showStatusModal?.fullName}"?
            </p>
          }
          onYesBtnText="Confirm"
          onNoBtnText="Cancel"
          onYesBtnVariant="primary"
          isProcessing={isPerformingAction === EActionItem.TOGGLE_STATUS}
        />
      )}
    </>
  );
};

export default UsersTable;
